import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScanLine, Smartphone, Loader2 } from "lucide-react";
import { API } from "@/apiConfig";
import { requestPhoneFromPinpad, friendlyPinpadError } from "@/lib/pinpadPhone";

const H = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const EMPTY = { full_name: "", first_name: "", last_name: "", dob: "", phone: "", email: "", license_number: "", address: "", city: "", state: "", zip: "", license_issued: "", license_expires: "", notes: "" };
const FIELDS = [["full_name", "Full name"], ["dob", "Date of birth", "date"], ["phone", "Phone"], ["email", "Email"], ["license_number", "License #"], ["address", "Address"], ["city", "City"], ["state", "State"], ["zip", "Zip"], ["license_issued", "Issued", "date"], ["license_expires", "Expires", "date"], ["notes", "Notes"]];

// Add / edit a customer. Scanning the ID (PDF417) into the scan box auto-fills every field the license
// carries; the phone can be typed or requested from the pinpad (PAX or ModeskP1/Dejavoo, whichever
// terminal is active — "Enter Phone Number").
export function CustomerDialog({ open, onClose, customer, onSaved, stationId, allowCreateOnly = false }) {
  const [form, setForm] = useState(EMPTY);
  const [scan, setScan] = useState("");
  const [busy, setBusy] = useState("");
  const [age, setAge] = useState(null);
  const scanRef = useRef(null);

  useEffect(() => { if (open) { setForm(customer ? { ...EMPTY, ...customer } : EMPTY); setScan(""); setAge(null); setTimeout(() => scanRef.current?.focus(), 150); } }, [open, customer]);

  const applyScan = async (raw) => {
    if (!raw.trim()) return;
    setBusy("scan");
    try {
      const r = await axios.post(`${API}/customers/parse-license`, { raw }, H());
      const p = r.data.parsed;
      setForm((f) => ({ ...f, full_name: p.full_name || f.full_name, first_name: p.first_name || "", last_name: p.last_name || "", dob: p.dob || f.dob, license_number: p.license_number || f.license_number, address: p.address || f.address, city: p.city || f.city, state: p.state || f.state, zip: p.zip || f.zip, license_issued: p.license_issued || f.license_issued, license_expires: p.license_expires || f.license_expires }));
      setAge(p.age ?? null);
      if (r.data.existing && !customer) toast.info(`${r.data.existing.full_name} is already a customer`);
      else toast.success("ID scanned — check the fields and add the phone number");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read that scan"); }
    setBusy(""); setScan("");
  };
  const paxPhone = async () => {
    setBusy("pax");
    try { const phone = await requestPhoneFromPinpad(stationId); setForm((f) => ({ ...f, phone })); toast.success("Phone number received from pinpad"); }
    catch (e) { toast.error(friendlyPinpadError(e)); }
    setBusy("");
  };
  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Name is required");
    setBusy("save");
    try {
      const r = customer?.id && !allowCreateOnly ? await axios.put(`${API}/customers/${customer.id}`, form, H()) : await axios.post(`${API}/customers`, form, H());
      toast.success(customer?.id ? "Customer updated" : "Customer added"); onSaved?.(r.data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="customer-dialog">
        <DialogHeader><DialogTitle>{customer?.id ? "Edit Customer" : "Add Customer"}{age != null && <span className={`ml-3 text-sm px-2 py-0.5 rounded ${age >= 21 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="customer-age-badge">Age {age}</span>}</DialogTitle></DialogHeader>
        <div className="flex gap-2 items-center bg-zinc-50 border border-dashed border-zinc-300 rounded-lg p-2">
          <ScanLine className="w-5 h-5 text-zinc-500 shrink-0" />
          <textarea ref={scanRef} value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); applyScan(scan); } }}
            onBlur={() => scan.length > 40 && applyScan(scan)} placeholder="Scan the barcode on the back of the driver's license here…" rows={1}
            className="flex-1 text-xs font-mono bg-transparent outline-none resize-none" data-testid="customer-scan-input" />
          {busy === "scan" && <Loader2 className="w-4 h-4 animate-spin" />}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {FIELDS.map(([k, label, type]) => (
            <label key={k} className={`text-xs text-zinc-600 ${k === "full_name" || k === "address" || k === "notes" ? "sm:col-span-2" : ""}`}>{label}
              <div className="flex gap-1">
                <Input type={type || "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-8 mt-0.5" data-testid={`customer-field-${k}`} />
                {k === "phone" && <Button type="button" size="sm" variant="outline" className="h-8 mt-0.5 shrink-0" disabled={!!busy} onClick={paxPhone} title="Ask the customer to enter it on the pinpad" data-testid="customer-pax-phone-btn">{busy === "pax" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Smartphone className="w-3.5 h-3.5" />}<span className="ml-1 hidden sm:inline">Pinpad</span></Button>}
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} data-testid="customer-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy === "save"} className="bg-zinc-900 text-white" data-testid="customer-save-btn">{busy === "save" ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
